import { GetAway, type GetAwayHotel, type GetAwaySpa } from './GetAway'
import type { GetAwayWithAvailability } from './GetAwayWithAvailability'
import type { Location } from './Location'
import { Money } from './Money'

export class GetAwayGroupByHotel {
  constructor(
    public hotel: GetAwayHotel,
    public spa: GetAwaySpa,
    public getAways: Array<GetAwayWithAvailability>,
  ) {}

  public static fromSameSpaGetAways(list: Array<GetAwayWithAvailability>) {
    if (!list.length) {
      throw new Error('GetAwayGroupByHotel::fromSameSpaGetAways list needs at least one element')
    }

    const sample = list[0]
    const hotel = sample.getAway.hotel
    const spa  = sample.getAway.spa

    return new GetAwayGroupByHotel(hotel, spa, list)
  }

  public static hasAnyValidAvailability(self: GetAwayGroupByHotel) {
    for (const ga of self.getAways) {
      if (ga.availability && ga.availability.bestRate) {
        return true
      }
    }

    return false
  }

  public static hasTagUUID(self: GetAwayGroupByHotel, tagUUID: string): boolean {
    for (const ga of self.getAways) {
      if (GetAway.hasTagUUID(ga.getAway, tagUUID)) {
        return true
      }
    }

    return false
  }

  public static hasWithTagUUIDAndValidAvailability(self: GetAwayGroupByHotel, tagUUID: string): boolean {
    for (const ga of self.getAways) {
      if (GetAway.hasTagUUID(ga.getAway, tagUUID) && ga.availability && ga.availability.bestRate) {
        return true
      }
    }

    return false
  }

  public static getHotelStarCount(self: GetAwayGroupByHotel): number {
    return self.hotel.category
  }

  public static getLowestPrice(self: GetAwayGroupByHotel): Money {
    if (!this.hasAnyValidAvailability(self)) {
      return new Money(0, 'EUR')
    }

    let min = Infinity
    for (const ga of self.getAways) {
      if (ga.availability && ga.availability.bestRate) {
        const price = ga.availability.bestRate.basePriceWithDiscount
        if (price.amount < min) {
          min = price.amount
        }
      }
    }

    return new Money(min, 'EUR')
  }

  public static getHighestPrice(self: GetAwayGroupByHotel): Money {
    if (!this.hasAnyValidAvailability(self)) {
      return new Money(0, 'EUR')
    }

    let max = 0
    for (const ga of self.getAways) {
      if (ga.availability && ga.availability.bestRate) {
        const price = ga.availability.bestRate.basePriceWithDiscount
        if (price.amount > max) {
          max = price.amount
        }
      }
    }

    return new Money(max, 'EUR')
  }

  public static includesSpa(self:  GetAwayGroupByHotel): boolean {
    for (const ga of self.getAways) {
      if (ga.getAway.includesSpa) {
        return true
      }
    }

    return false
  }
}

export class GetAwayGroupByHotelCollection {
  private withAvailability: Array<GetAwayGroupByHotel>
  private withoutAvailability: Array<GetAwayGroupByHotel>

  constructor(groups: Array<GetAwayGroupByHotel>) {
    this.withAvailability = []
    this.withoutAvailability = []

    groups.forEach((group) => {
      if (GetAwayGroupByHotel.hasAnyValidAvailability(group)) {
        this.withAvailability.push(group)
        return
      }

      this.withoutAvailability.push(group)
    })
  }

  public get() {
    return this.withAvailability.concat(this.withoutAvailability)
  }

  public filterByCountry(country: Location) {
    const filterByCountry = (item: GetAwayGroupByHotel) => item.hotel.countryCode === country.countryCode
    this.withAvailability = this.withAvailability.filter(filterByCountry)
    this.withoutAvailability = this.withoutAvailability.filter(filterByCountry)
  }

  public filterByOnlyHotel() {
    this.withAvailability = this.filterByOnlyHotelImpl(this.withAvailability)
    this.withoutAvailability = this.filterByOnlyHotelImpl(this.withoutAvailability)
  }

  private filterByOnlyHotelImpl(list: Array<GetAwayGroupByHotel>) {
    return list.filter((a) => !GetAwayGroupByHotel.includesSpa(a))
  }

  public filterByHotelStarCount(stars: Array<number>) {
    function filterByStars(ga: GetAwayGroupByHotel) {
      const hotelStars = ga.hotel.category

      return stars.includes(hotelStars)
    }

    this.withAvailability = this.withAvailability.filter(filterByStars)
    this.withoutAvailability = this.withoutAvailability.filter(filterByStars)
  }

  public filterByIncludesTagUUIDs(tagUUIDs: Array<string>) {
    const withAvail: Record<string, GetAwayGroupByHotel> = {}
    const withoutAvail: Record<string, GetAwayGroupByHotel> = {}

    for (const group of this.get()) {
      for (const uuid of tagUUIDs) {
        if (withAvail[group.hotel.uuid] || withoutAvail[group.hotel.uuid]) {
          break
        }
        for (const ga of group.getAways) {
          if (!GetAway.hasTagUUID(ga.getAway, uuid)) {
            continue
          }

          if (ga.availability && ga.availability.bestRate) {
            withAvail[group.hotel.uuid] = (group)
          } else {
            withoutAvail[group.hotel.uuid] = (group)
          }

          break
        }
      }
    }

    this.withAvailability = Object.values(withAvail)
    this.withoutAvailability = Object.values(withoutAvail)
  }

  public filterByIncludesTagUUIDsAndAvailability(tagUUIDs: Array<string>) {
    const withAvail: Record<string, GetAwayGroupByHotel> = {}
    const withoutAvail: Record<string, GetAwayGroupByHotel> = {}

    for (const group of this.get()) {
      for (const uuid of tagUUIDs) {
        if (GetAwayGroupByHotel.hasWithTagUUIDAndValidAvailability(group, uuid)) {
          withAvail[group.hotel.uuid] = group
        } else if (GetAwayGroupByHotel.hasTagUUID(group, uuid)) {
          withoutAvail[group.hotel.uuid] = group
        }
      }
    }

    this.withAvailability = Object.values(withAvail)
    this.withoutAvailability = Object.values(withoutAvail)
  }

  public filterByHasAvailabilityIfThereIsAny() {
    if (this.withAvailability.length) {
      this.withoutAvailability = []
    }
  }

  public sortByHotelStars() {
    function sortImpl(a: GetAwayGroupByHotel, b: GetAwayGroupByHotel) {
      const aStars = GetAwayGroupByHotel.getHotelStarCount(a)
      const bStars = GetAwayGroupByHotel.getHotelStarCount(b)

      if (aStars < bStars) return 1
      if (aStars > bStars) return -1

      return 0
    }

    this.withAvailability = this.withAvailability.sort(sortImpl)
    this.withoutAvailability = this.withoutAvailability.sort(sortImpl)
  }

  public sortByCheaper() {
    this.withAvailability = this.withAvailability
      .sort((a, b) => {
        const amountA = GetAwayGroupByHotel.getLowestPrice(a).amount
        const amountB = GetAwayGroupByHotel.getLowestPrice(b).amount

        if (!amountA || !amountB) {
          return 0
        }

        if (amountA < amountB) return -1
        if (amountA > amountB) return 1

        return 0
      })
  }

  public sortByMostExpensive() {
    this.withAvailability = this.withAvailability
      .sort((a, b) => {
        const amountA = GetAwayGroupByHotel.getHighestPrice(a).amount
        const amountB = GetAwayGroupByHotel.getHighestPrice(b).amount

        if (!amountA || !amountB) {
          return 0
        }

        if (amountA < amountB) return 1
        if (amountA > amountB) return -1

        return 0
      })
  }

  public spliceByIncludesSpa() {
    this.withAvailability = this.sortByIncludesSpaImpl(this.withAvailability)
    this.withoutAvailability = this.sortByIncludesSpaImpl(this.withoutAvailability)
  }

  private sortByIncludesSpaImpl(list: Array<GetAwayGroupByHotel>) {
    const withSpa: Array<GetAwayGroupByHotel> = []
    const withoutSpa: Array<GetAwayGroupByHotel> = []

    list.forEach((a) => {
      if (GetAwayGroupByHotel.includesSpa(a)) {
        withSpa.push(a)
        return
      }

      withoutSpa.push(a)
    })

    return withSpa.concat(withoutSpa)
  }
}
